import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Select, DatePicker, Table, InputNumber, Space, message, Card, Tag, Alert, Tooltip, Checkbox, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined, CheckCircleOutlined, ExclamationCircleOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Customer, Product, SelectedStock, CustomerQuote, QuoteStatus, ShippingDestination } from '../types.ts';
import { productAPI, customerQuoteAPI, shippingDestinationAPI } from '../services/api.ts';
import ProductAvailabilityModal from './ProductAvailabilityModal.tsx';
import { formatCurrency, inputNumberFormatter, inputNumberParser } from '../utils/numberFormatting.ts';
import { formatProductDisplay } from '../utils/productUtils.ts';
import { QUOTE_CREATABLE_STATUS_IDS, QUOTE_DEFAULT_CREATE_STATUS_ID, getQuoteStatusColor } from '../utils/quoteStatus.ts';

const { Option } = Select;
const { TextArea } = Input;

// Form preventivo cliente, allineato a CustomerOrderForm: numeri in maiuscolo, prodotti
// filtrati per cliente, stato solo in creazione (in modifica si usa "Cambia Stato"),
// campi che confluiscono nell'ordine (riferimento cliente, consegna richiesta,
// destinazione, Ordine Stock), modalità "solo testata" dopo l'accettazione.
interface CustomerQuoteFormProps {
  initialValues?: CustomerQuote | null;
  customers: Customer[];
  quoteStatuses: QuoteStatus[];
  onSubmit: (values: any) => void;
  onCancel: () => void;
  /** Preventivo accettato: righe in sola lettura, si salva solo la testata */
  headerOnly?: boolean;
}

interface QuoteRow {
  key: number;
  product_id?: number;
  quantity: number;
  unit_price: number;
  total_price: number;
  notes: string;
}

interface AvailabilityInfo {
  available: number;
  inProduction: number;
  futureAvailable: number;
  error?: string;
}

const CustomerQuoteForm: React.FC<CustomerQuoteFormProps> = ({
  initialValues,
  customers,
  quoteStatuses,
  onSubmit,
  onCancel,
  headerOnly = false,
}) => {
  const [form] = Form.useForm();
  const [products, setProducts] = useState<Product[]>([]);
  const [shippingDestinations, setShippingDestinations] = useState<ShippingDestination[]>([]);
  const [rows, setRows] = useState<QuoteRow[]>([]);
  const [filterByCustomer, setFilterByCustomer] = useState(true);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [availability, setAvailability] = useState<{ [productId: number]: AvailabilityInfo }>({});
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityModalData, setAvailabilityModalData] = useState<{
    productId: number;
    productName: string;
    originalPrice: number;
    rowIndex: number;
  } | null>(null);

  const loadProducts = useCallback(async (customerId?: number) => {
    try {
      const data = await productAPI.getProducts(undefined, customerId);
      setProducts(data);
    } catch (error) {
      message.error('Errore nel caricamento dei prodotti');
    }
  }, []);

  const loadShippingDestinations = useCallback(async (customerId: number) => {
    try {
      const data = await shippingDestinationAPI.getShippingDestinations('', customerId);
      setShippingDestinations(data || []);
    } catch (error) {
      setShippingDestinations([]);
    }
  }, []);

  // Disponibilità SOLO su richiesta (cambio prodotto, apertura, pulsante Aggiorna):
  // prima partiva una chiamata per prodotto a ogni tasto.
  const fetchAvailability = useCallback(async (productIds: Array<number | undefined>) => {
    const ids = Array.from(new Set(productIds.filter((id): id is number => !!id)));
    if (ids.length === 0) return;
    setCheckingAvailability(true);
    try {
      const results = await Promise.all(ids.map(async (productId) => {
        try {
          const a = await customerQuoteAPI.checkProductAvailability(productId);
          const info: AvailabilityInfo = {
            available: a.real_available ?? a.available ?? 0,
            inProduction: a.in_production ?? 0,
            futureAvailable: a.future_available ?? 0,
          };
          return [productId, info] as [number, AvailabilityInfo];
        } catch {
          return [productId, { available: 0, inProduction: 0, futureAvailable: 0, error: 'Errore nel controllo disponibilità' }] as [number, AvailabilityInfo];
        }
      }));
      setAvailability((prev) => {
        const next = { ...prev };
        results.forEach(([productId, info]) => { next[productId] = info; });
        return next;
      });
    } finally {
      setCheckingAvailability(false);
    }
  }, []);

  useEffect(() => {
    if (initialValues) {
      setSelectedCustomerId(initialValues.customer_id);
      loadShippingDestinations(initialValues.customer_id);
      const mapped: QuoteRow[] = (initialValues.quote_details || []).map((d, index) => ({
        key: d.id ?? index,
        product_id: d.product_id,
        quantity: d.quantity,
        unit_price: Number(d.unit_price ?? 0),
        total_price: Number(d.total_price ?? d.quantity * Number(d.unit_price ?? 0)),
        notes: d.notes || '',
      }));
      setRows(mapped);
      fetchAvailability(mapped.map((r) => r.product_id));
      form.setFieldsValue({
        customer_id: initialValues.customer_id,
        quote_number: initialValues.quote_number,
        customer_reference: initialValues.customer_reference || undefined,
        quote_status_id: initialValues.quote_status_id,
        quote_date: initialValues.quote_date ? dayjs(initialValues.quote_date) : null,
        expiry_date: initialValues.expiry_date ? dayjs(initialValues.expiry_date) : null,
        requested_delivery_date: initialValues.requested_delivery_date ? dayjs(initialValues.requested_delivery_date) : null,
        shipping_destination_id: initialValues.shipping_destination_id || undefined,
        reorder: !!initialValues.reorder,
        notes: initialValues.notes || undefined,
      });
    } else {
      form.setFieldsValue({
        quote_date: dayjs(),
        expiry_date: dayjs().add(30, 'day'),
        quote_status_id: QUOTE_DEFAULT_CREATE_STATUS_ID,
        reorder: false,
      });
    }
  }, [initialValues, form, loadShippingDestinations, fetchAvailability]);

  // Prodotti filtrati per cliente (come nel form ordine cliente)
  useEffect(() => {
    loadProducts(filterByCustomer && selectedCustomerId ? selectedCustomerId : undefined);
  }, [filterByCustomer, selectedCustomerId, loadProducts]);

  const handleCustomerChange = (customerId: number) => {
    setSelectedCustomerId(customerId);
    loadShippingDestinations(customerId);
    form.setFieldValue('shipping_destination_id', undefined);
  };

  const addRow = () => {
    setRows([...rows, { key: Date.now(), product_id: undefined, quantity: 1, unit_price: 0, total_price: 0, notes: '' }]);
  };

  const removeRow = (index: number) => {
    setRows(rows.filter((_, i) => i !== index));
  };

  const updateRow = (index: number, field: keyof QuoteRow, value: any) => {
    const current = rows[index];
    const updated: QuoteRow = { ...current, [field]: value };
    if (field === 'product_id') {
      const product = products.find((p) => p.id === value);
      if (product && product.price != null) {
        updated.unit_price = Number(product.price);
      }
      if (value) fetchAvailability([value]);
    }
    updated.total_price = (updated.quantity || 0) * (updated.unit_price || 0);
    const next = [...rows];
    next[index] = updated;
    setRows(next);
  };

  const handleOpenStockModal = (record: QuoteRow, index: number) => {
    if (!record.product_id) {
      message.warning('Seleziona prima un prodotto');
      return;
    }
    const product = products.find((p) => p.id === record.product_id);
    setAvailabilityModalData({
      productId: record.product_id,
      productName: product ? formatProductDisplay(product) : `ID ${record.product_id}`,
      originalPrice: record.unit_price || Number(product?.price || 0),
      rowIndex: index,
    });
  };

  // Dal modal giacenze: UNA riga per prodotto con la quantità sommata degli stock scelti e
  // le partite nelle note (prima una riga per stock: stesso prodotto su più righe, che il
  // server rifiuta sempre con 400 "un prodotto per riga"; il preventivo non prenota nulla,
  // la scelta serve solo a vedere la disponibilità)
  const handleStockSelected = (selectedStocks: SelectedStock[]) => {
    if (!availabilityModalData) return;
    const { rowIndex, productId, originalPrice } = availabilityModalData;
    if (selectedStocks.length === 0) {
      setAvailabilityModalData(null);
      return;
    }
    const quantity = selectedStocks.reduce((sum, s) => sum + (s.qta_desired || 0), 0);
    const notes = Array.from(new Set(selectedStocks.map((s) => s.stock_code || 'da Produrre'))).join(', ');
    const next = [...rows];
    next.splice(rowIndex, 1, {
      key: Date.now(),
      product_id: productId,
      quantity,
      unit_price: originalPrice,
      total_price: quantity * originalPrice,
      notes,
    });
    setRows(next);
    setAvailabilityModalData(null);
  };

  const renderAvailability = (record: QuoteRow) => {
    if (!record.product_id) return null;
    const info = availability[record.product_id];
    if (!info) return <Tag>-</Tag>;
    if (info.error) return <Tag icon={<ExclamationCircleOutlined />} color="error">Errore</Tag>;
    const requested = record.quantity || 0;
    let tag: React.ReactNode;
    if (info.available >= requested) {
      tag = <Tag icon={<CheckCircleOutlined />} color="success">Disponibile ({info.available})</Tag>;
    } else if (info.futureAvailable >= requested && info.inProduction > 0) {
      tag = <Tag icon={<ExclamationCircleOutlined />} color="warning">Futuro ({info.available}+{info.inProduction})</Tag>;
    } else {
      tag = <Tag icon={<ExclamationCircleOutlined />} color="error">Insufficiente ({info.available}/{requested})</Tag>;
    }
    return (
      <Tooltip title={
        <div>
          <div>Disponibile: {info.available}</div>
          <div>In produzione: {info.inProduction}</div>
          <div>Richiesto: {requested}</div>
          <div style={{ marginTop: 8 }}><strong>Totali prodotto su tutti i magazzini</strong></div>
        </div>
      }>
        {tag}
      </Tooltip>
    );
  };

  const handleSubmit = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!headerOnly) {
      if (rows.length === 0) {
        message.error('Aggiungi almeno un prodotto al preventivo');
        return;
      }
      const invalidRow = rows.findIndex((r) => !r.product_id || !(r.quantity >= 1));
      if (invalidRow >= 0) {
        message.error(`Riga ${invalidRow + 1}: selezionare un prodotto e indicare una quantità di almeno 1`);
        return;
      }
    }
    const payload: any = {
      customer_id: values.customer_id,
      quote_number: values.quote_number,
      customer_reference: values.customer_reference || null,
      quote_date: values.quote_date ? values.quote_date.format('YYYY-MM-DD') : null,
      expiry_date: values.expiry_date ? values.expiry_date.format('YYYY-MM-DD') : null,
      requested_delivery_date: values.requested_delivery_date ? values.requested_delivery_date.format('YYYY-MM-DD') : null,
      shipping_destination_id: values.shipping_destination_id || null,
      reorder: !!values.reorder,
      notes: values.notes || null,
    };
    // Lo stato si invia SOLO in creazione: in modifica passa da "Cambia Stato"
    if (!initialValues) {
      payload.quote_status_id = values.quote_status_id || QUOTE_DEFAULT_CREATE_STATUS_ID;
    }
    // Preventivo accettato: le righe non si inviano (il backend le rifiuterebbe)
    if (!headerOnly) {
      payload.quote_details = rows.map((r) => ({
        product_id: r.product_id,
        quantity: r.quantity,
        unit_price: r.unit_price,
        notes: r.notes || null,
      }));
    }
    onSubmit(payload);
  };

  const creatableStatuses = quoteStatuses.filter((s) => QUOTE_CREATABLE_STATUS_IDS.includes(s.id));
  const totalAmount = rows.reduce((sum, r) => sum + (r.total_price || 0), 0);

  const columns = [
    {
      title: 'Prodotto',
      dataIndex: 'product_id',
      width: '38%',
      ellipsis: true,
      render: (_: any, record: QuoteRow, index: number) => (
        <Select
          value={record.product_id}
          onChange={(value) => updateRow(index, 'product_id', value)}
          style={{ width: '100%' }}
          placeholder="Seleziona prodotto"
          showSearch
          optionFilterProp="children"
          disabled={headerOnly}
        >
          {products.map((product) => (
            <Option key={product.id} value={product.id} title={formatProductDisplay(product)}>
              {formatProductDisplay(product)}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Qta',
      dataIndex: 'quantity',
      width: '8%',
      render: (_: any, record: QuoteRow, index: number) => (
        <InputNumber value={record.quantity} onChange={(value) => updateRow(index, 'quantity', value || 0)} min={1} style={{ width: '100%' }} size="small" disabled={headerOnly} />
      ),
    },
    {
      title: 'Vendita Unit.',
      dataIndex: 'unit_price',
      width: '12%',
      render: (_: any, record: QuoteRow, index: number) => (
        <InputNumber
          value={record.unit_price}
          onChange={(value) => updateRow(index, 'unit_price', value || 0)}
          min={0}
          precision={4}
          step={0.0001}
          style={{ width: '100%' }}
          formatter={inputNumberFormatter}
          parser={inputNumberParser}
          placeholder="0,0000"
          size="small"
          disabled={headerOnly}
        />
      ),
    },
    { title: 'Totale', dataIndex: 'total_price', width: '10%', render: (value: number) => `€ ${formatCurrency(value || 0)}` },
    { title: 'Disponibilità', key: 'availability', width: '14%', render: (_: any, record: QuoteRow) => renderAvailability(record) },
    {
      title: 'Note',
      dataIndex: 'notes',
      width: '12%',
      render: (_: any, record: QuoteRow, index: number) => (
        <Input value={record.notes} onChange={(e) => updateRow(index, 'notes', e.target.value)} placeholder="Note" size="small" disabled={headerOnly} />
      ),
    },
    {
      title: 'Azioni',
      width: '6%',
      render: (_: any, record: QuoteRow, index: number) => (
        <Space size="small">
          {!headerOnly && (
            <Button type="text" size="small" icon={<SearchOutlined />} onClick={() => handleOpenStockModal(record, index)} title="Giacenze del prodotto" />
          )}
          {!headerOnly && (
            <Popconfirm title="Rimuovere questa riga?" onConfirm={() => removeRow(index)} okText="Sì" cancelText="No">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Form form={form} layout="vertical">
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {headerOnly && (
            <Alert
              type="info"
              showIcon
              message="Preventivo accettato"
              description="Si può modificare solo la testata: le righe dei prodotti sono in sola lettura (per cambiare i prodotti usare Duplica)."
            />
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <Form.Item label="Cliente" name="customer_id" rules={[{ required: true, message: 'Seleziona un cliente' }]}>
              <Select placeholder="Seleziona cliente" onChange={handleCustomerChange} showSearch optionFilterProp="children" disabled={headerOnly}>
                {customers.map((customer) => (
                  <Option key={customer.id} value={customer.id}>{customer.company}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Numero Preventivo" name="quote_number" rules={[{ required: true, message: 'Inserisci il numero preventivo' }, { max: 50, message: 'Massimo 50 caratteri' }]}>
              <Input
                placeholder="Numero preventivo"
                style={{ textTransform: 'uppercase' }}
                onChange={(e) => form.setFieldValue('quote_number', e.target.value.toUpperCase())}
              />
            </Form.Item>

            <Form.Item label="Riferimento Cliente" name="customer_reference" rules={[{ max: 50, message: 'Massimo 50 caratteri' }]} tooltip="Riferimento/richiesta del cliente: alla conversione diventa il Numero Ordine Cliente">
              <Input
                placeholder="Riferimento cliente (opzionale)"
                style={{ textTransform: 'uppercase' }}
                onChange={(e) => form.setFieldValue('customer_reference', e.target.value.toUpperCase())}
              />
            </Form.Item>

            <Form.Item label="Data Preventivo" name="quote_date" rules={[{ required: true, message: 'Seleziona la data preventivo' }]}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>

            <Form.Item label="Data Scadenza" name="expiry_date">
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>

            <Form.Item label="Data Consegna Richiesta" name="requested_delivery_date">
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>

            <Form.Item
              label="Stato Preventivo"
              name="quote_status_id"
              rules={[{ required: true, message: 'Seleziona uno stato' }]}
              tooltip={initialValues
                ? "Lo stato non si modifica da qui: usare l'azione 'Cambia Stato'"
                : 'In creazione un preventivo può nascere in Bozza, Nuovo o Inviato'}
            >
              <Select placeholder="Seleziona stato" disabled={!!initialValues}>
                {(initialValues ? quoteStatuses : creatableStatuses).map((status) => (
                  <Option key={status.id} value={status.id}>
                    <Tag color={getQuoteStatusColor(status)}>{status.display_name}</Tag>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Destinazione Spedizione" name="shipping_destination_id" tooltip="Meta di consegna del cliente (Anagrafica → Destinazioni Cliente): alla conversione passa sull'ordine">
              <Select placeholder="Seleziona destinazione" allowClear disabled={!selectedCustomerId}>
                {shippingDestinations.map((destination) => (
                  <Option key={destination.id} value={destination.id}>{destination.name}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="reorder" valuePropName="checked" style={{ gridColumn: 'span 2' }}>
              <Checkbox>
                <strong>Ordine Stock</strong> - Forza creazione ordini fornitori anche se i prodotti sono disponibili in stock
              </Checkbox>
            </Form.Item>
          </div>

          <Form.Item label="Note" name="notes">
            <TextArea rows={3} placeholder="Note sul preventivo..." />
          </Form.Item>

          <div>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Dettagli Prodotti</h3>
              <Space>
                <Checkbox checked={filterByCustomer} onChange={(e) => setFilterByCustomer(e.target.checked)} disabled={!selectedCustomerId}>
                  <strong>Visualizza solo i prodotti associati al cliente</strong>
                </Checkbox>
                <Button icon={<ReloadOutlined />} onClick={() => fetchAvailability(rows.map((r) => r.product_id))} loading={checkingAvailability} disabled={rows.length === 0}>
                  Aggiorna disponibilità
                </Button>
                {!headerOnly && (
                  <Button icon={<PlusOutlined />} onClick={addRow}>Aggiungi Prodotto</Button>
                )}
              </Space>
            </div>

            <Table
              columns={columns}
              dataSource={rows}
              rowKey="key"
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
              bordered
              tableLayout="fixed"
              locale={{ emptyText: 'Nessun prodotto aggiunto' }}
            />

            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <strong>Totale Preventivo: € {formatCurrency(totalAmount)}</strong>
            </div>
            <Card size="small" style={{ marginTop: 12 }}>
              La disponibilità è calcolata sui totali del prodotto (tutti i magazzini) e aggiornata alla scelta del prodotto o con "Aggiorna disponibilità".
            </Card>
          </div>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={onCancel}>Annulla</Button>
              <Button type="primary" onClick={handleSubmit}>
                {initialValues ? 'Aggiorna' : 'Crea'} Preventivo
              </Button>
            </Space>
          </div>
        </Space>
      </Form>

      <ProductAvailabilityModal
        visible={availabilityModalData !== null}
        onClose={() => setAvailabilityModalData(null)}
        productId={availabilityModalData?.productId || 0}
        productName={availabilityModalData?.productName || ''}
        originalPrice={availabilityModalData?.originalPrice || 0}
        onStockSelected={handleStockSelected}
      />
    </div>
  );
};

export default CustomerQuoteForm;
